import React, { useEffect, useRef, useState } from "react";
import { useOutsideAlerter } from "../../util/useOutsideAlerter";

// Kit dropdown: the one "pick from a list" control (replaces native <select>).
// Trigger = kit field (or a chip for inline spots: variant="chip"), kit chevron, list = kit menu.
// Keyboard: Enter / Space / ArrowDown opens, arrows move, Enter picks, Escape closes.
// options: [{ value, label, disabled? }]. renderValue / renderOption customise what's shown.
// For "type to search with suggestions" use DropDownInput (same look, different behaviour).
const Dropdown = ({
  value,
  options = [],
  onChange,
  placeholder = "select…",
  variant = "field",
  size = "md",
  error = false,
  disabled = false,
  renderValue,
  renderOption,
  className = "",
  menuClassName = "",
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  useOutsideAlerter(wrapperRef, () => setOpen(false));

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = options[selectedIndex];
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i > -1);

  useEffect(() => {
    if (open) setActive(selectedIndex > -1 ? selectedIndex : enabled[0] ?? -1);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.children?.[active]?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const pick = (option) => {
    if (!option || option.disabled) return;
    onChange?.(option.value, option);
    setOpen(false);
  };

  const move = (dir) => {
    if (!enabled.length) return;
    const pos = enabled.indexOf(active);
    setActive(enabled[(pos + dir + enabled.length) % enabled.length] ?? enabled[0]);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && ["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") (e.preventDefault(), move(1));
    else if (e.key === "ArrowUp") (e.preventDefault(), move(-1));
    else if (e.key === "Enter" || e.key === " ") (e.preventDefault(), pick(options[active]));
    else if (e.key === "Escape" || e.key === "Tab") setOpen(false);
  };

  const triggerClass =
    variant === "chip"
      ? `inyc-dropdown-trigger inyc-btn-chip ${open ? "is-active" : ""}`
      : `inyc-dropdown-trigger inyc-field ${size === "sm" ? "is-sm" : ""} ${error ? "is-error" : ""}`;

  return (
    <div ref={wrapperRef} className={`inyc-dropdown ${variant === "chip" ? "is-chip" : ""} ${className}`.trim()}>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error || undefined}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={triggerClass.trim()}
        {...rest}
      >
        <span
          className={`min-w-0 flex-1 truncate ${
            variant === "chip"
              ? "font-['Primary-Medium'] text-kit-xs"
              : selected
                ? "font-['Primary-Medium'] text-kit-base text-fg"
                : "font-['Primary-Regular'] text-kit-sm text-fg-muted"
          }`}
        >
          {selected ? (renderValue ? renderValue(selected) : selected.label) : placeholder}
        </span>
        <span aria-hidden="true" className={`inyc-field-chevron ${open ? "is-open" : ""} ${variant === "chip" ? "!ml-0 !h-[0.75rem] !w-[0.75rem] !bg-[length:0.75rem]" : ""}`} />
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          className={`inyc-menu ${variant === "chip" ? "" : "is-field left-0 right-0"} absolute top-full z-50 mt-kit-4 max-h-[18rem] overflow-y-auto ${menuClassName}`.trim()}
        >
          {options.map((o, i) => (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={o.value === value}
              disabled={o.disabled}
              onMouseEnter={() => !o.disabled && setActive(i)}
              onClick={() => pick(o)}
              className={`inyc-menu-item ${o.value === value ? "is-selected" : i === active ? "bg-hover" : ""} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              {renderOption ? renderOption(o) : o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
